// src/components/login/admin/admin-users-page.tsx
import { useEffect, useMemo, useState } from "react";
import { authApi, type UserMe } from "@/api/auth";
import { brandsApi, type DeletionImpact } from "@/api/brands";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Alert, AlertDescription } from "../../ui/alert";
import { ConfirmDialog } from "../../ui/confirm-dialog";
import { AuditLogDialog } from "./audit-log-dialog";
import { CreateUserPage } from "./create-user-page";
import { USER_TYPES, DEFAULT_USER_TYPE } from "./user-types";
import { DeletionImpactDescription } from "./deletion-impact-description";
import { FeatureAccessPanel } from "../../navigation/feature-access-panel";
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  Pencil,
  Save,
  RefreshCcw,
  History,
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  User as UserIcon,
  Bot,
  Search,
  X,
  KeyRound,
  Trash2,
  UserPlus,
} from "lucide-react";

type Mode = "list" | "edit" | "create";
type RoleFilter = "all" | "basic" | "standard" | "agent" | "admin";

function isAdminFromStorage() {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

function currentUsernameFromStorage(): string {
  return (
    localStorage.getItem("username") ||
    sessionStorage.getItem("username") ||
    ""
  );
}

// ─── Badges ──────────────────────────────────────────────────────────────────

function UserTypeBadge({ userType }: { userType?: string }) {
  const t = userType || "basic";
  const cfg =
    t === "admin"
      ? { icon: ShieldCheck, color: "var(--alpha-teal)",       bg: "var(--alpha-teal-10)",  border: "var(--alpha-teal-20)",  label: "Admin" }
      : t === "agent"
      ? { icon: Bot,         color: "var(--spectral-violet)",  bg: "rgba(139,92,246,0.08)", border: "rgba(139,92,246,0.2)", label: "Agent" }
      : t === "basic"
      ? { icon: UserIcon,    color: "var(--muted-foreground)", bg: "var(--muted)",           border: "var(--border)",         label: "Basic" }
      : { icon: UserIcon,    color: "var(--signal-blue)",      bg: "var(--signal-blue-10)",  border: "var(--signal-blue-25)", label: "Standard" };

  const Icon = cfg.icon;
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
      }}
    >
      <Icon style={{ width: 13, height: 13 }} />
      {cfg.label}
    </span>
  );
}

function AdminBadge({ isAdmin }: { isAdmin: boolean }) {
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: isAdmin ? "var(--alpha-teal-10)" : "var(--muted)",
        border: `1px solid ${isAdmin ? "var(--alpha-teal-20)" : "var(--border)"}`,
        color: isAdmin ? "var(--alpha-teal)" : "var(--muted-foreground)",
      }}
    >
      {isAdmin
        ? <ShieldCheck style={{ width: 14, height: 14 }} />
        : <UserIcon    style={{ width: 14, height: 14 }} />}
      {isAdmin ? "Admin: Yes" : "Admin: No"}
    </span>
  );
}

function MfaBadge({ user }: { user: UserMe }) {
  const enabled  = !!user.mfa_enabled;
  const required = !!user.mfa_required;

  if (enabled) {
    return (
      <span
        className="whitespace-nowrap"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 9px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          backgroundColor: "var(--alpha-teal-10)",
          border: "1px solid var(--alpha-teal-20)",
          color: "var(--alpha-teal)",
        }}
      >
        <ShieldCheck style={{ width: 13, height: 13 }} />
        MFA: Enabled
      </span>
    );
  }
  if (required) {
    return (
      <span
        className="whitespace-nowrap"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 9px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          backgroundColor: "var(--signal-blue-10)",
          border: "1px solid var(--signal-blue-25)",
          color: "var(--signal-blue)",
        }}
      >
        <ShieldAlert style={{ width: 13, height: 13 }} />
        MFA: Setup pending
      </span>
    );
  }
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: "var(--muted)",
        border: "1px solid var(--border)",
        color: "var(--muted-foreground)",
      }}
    >
      <ShieldOff style={{ width: 13, height: 13 }} />
      MFA: Off
    </span>
  );
}

// ─── User card ───────────────────────────────────────────────────────────────

function UserCard({
  user,
  onEdit,
  onFeatureAccess,
}: {
  user: UserMe;
  onEdit: (id: number) => void;
  onFeatureAccess: (user: UserMe) => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 p-4 rounded-xl border transition-all hover:shadow-md"
      style={{
        backgroundColor: "var(--card)",
        borderColor: "var(--border)",
      }}
    >
      {/* Header: avatar + name + email */}
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: user.is_admin ? "var(--alpha-teal-10)" : "var(--muted)",
            border: `1px solid ${user.is_admin ? "var(--alpha-teal-20)" : "var(--border)"}`,
          }}
        >
          {user.is_admin ? (
            <ShieldCheck style={{ width: 18, height: 18, color: "var(--alpha-teal)" }} />
          ) : (
            <UserIcon style={{ width: 18, height: 18, color: "var(--muted-foreground)" }} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div
            style={{
              fontWeight: 800,
              color: "var(--foreground)",
              lineHeight: 1.2,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              fontSize: 14,
            }}
          >
            {user.full_name || "—"}
          </div>
          <div
            title={user.username}
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              marginTop: 3,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.4,
            }}
          >
            {user.username}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              marginTop: 4,
              opacity: 0.7,
            }}
          >
            ID: {user.id}
          </div>
        </div>
      </div>

      {/* Badges row */}
      <div className="flex items-center gap-2 flex-wrap">
        <UserTypeBadge userType={user.user_type} />
        <AdminBadge isAdmin={!!user.is_admin} />
        <MfaBadge user={user} />
      </div>

      {/* Action */}
      <div
        className="pt-2 mt-auto"
        style={{ borderTop: "1px solid var(--border)" }}
      >
        <div className="flex gap-2">
          <Button
            onClick={() => onEdit(user.id)}
            variant="secondary"
            className="flex-1"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <Pencil style={{ width: 16, height: 16 }} />
            Edit user
          </Button>
          <Button
            onClick={() => onFeatureAccess(user)}
            variant="secondary"
            title="Feature access"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <KeyRound style={{ width: 16, height: 16 }} />
            Access
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const [mode,    setMode]    = useState<Mode>("list");
  const [featureAccessUser, setFeatureAccessUser] = useState<UserMe | null>(null);
  const [users,   setUsers]   = useState<UserMe[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [err,     setErr]     = useState("");
  const [success, setSuccess] = useState("");

  // ── Delete user (hard delete + dry-run preview) ──
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState<DeletionImpact | null>(null);
  const [previewingDelete, setPreviewingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) || null,
    [users, selectedId],
  );

  // Edit fields
  const [fullName,      setFullName]      = useState("");
  const [username,      setUsername]      = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [isAdmin,       setIsAdmin]       = useState(false);
  const [userType,      setUserType]      = useState<string>(DEFAULT_USER_TYPE);
  const [mfaRequired,   setMfaRequired]   = useState(true);

  // ── Search + filters ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [adminOnly, setAdminOnly] = useState(false);

  // Auth activity (audit log) viewer
  const [auditOpen, setAuditOpen] = useState(false);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (q) {
        const hay = `${u.full_name || ""} ${u.username || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (roleFilter !== "all" && (u.user_type || "basic") !== roleFilter) return false;
      if (adminOnly && !u.is_admin) return false;
      return true;
    });
  }, [users, searchQuery, roleFilter, adminOnly]);

  const hasActiveFilter = searchQuery.trim().length > 0 || roleFilter !== "all" || adminOnly;

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("all");
    setAdminOnly(false);
  };

  const loadUsers = async () => {
    setErr(""); setSuccess(""); setLoading(true);
    try {
      if (!isAdminFromStorage()) {
        setErr("Admin access required.");
        setUsers([]); setSelectedId(null);
        return;
      }
      const list = await authApi.listUsers();
      const sorted = [...list].sort((a, b) => {
        const aa = a.is_admin ? 0 : 1;
        const bb = b.is_admin ? 0 : 1;
        if (aa !== bb) return aa - bb;
        return (a.full_name || a.username).localeCompare(b.full_name || b.username);
      });
      setUsers(sorted);
      if (sorted.length && selectedId === null) setSelectedId(sorted[0].id);
    } catch (e: any) {
      setErr(e?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected) return;
    setUsername(selected.username || "");
    setFullName(selected.full_name || "");
    setResetPassword("");
    setIsAdmin(!!selected.is_admin);
    setUserType(selected.user_type || DEFAULT_USER_TYPE);
    setMfaRequired(selected.mfa_required ?? true);
    setSuccess(""); setErr("");
  }, [selected?.id]);

  const goEdit = (userId: number) => {
    setSelectedId(userId); setMode("edit"); setSuccess(""); setErr("");
  };

  const goList = () => { setMode("list"); setSuccess(""); setErr(""); };

  const onSave = async () => {
    if (!selected) return;
    setErr(""); setSuccess(""); setSaving(true);
    try {
      const payload: any = {};

      const newFullName = fullName.trim();
      if (newFullName !== (selected.full_name || "")) payload.full_name = newFullName;

      const newUsername = username.trim().toLowerCase();
      if (newUsername && newUsername !== selected.username) payload.username = newUsername;

      if (resetPassword.trim()) payload.password = resetPassword.trim();
      if (isAdmin !== selected.is_admin)            payload.is_admin  = isAdmin;

      const effectiveType = userType || DEFAULT_USER_TYPE;
      if (effectiveType !== (selected.user_type || DEFAULT_USER_TYPE)) {
        payload.user_type = effectiveType;
      }

      if (mfaRequired !== !!selected.mfa_required) {
        payload.mfa_required = mfaRequired;
      }

      if (Object.keys(payload).length === 0) { setSuccess("No changes to save."); return; }

      await authApi.updateUser(selected.id, payload);
      setSuccess("User updated successfully.");
      await loadUsers();
      setMode("list");
    } catch (e: any) {
      setErr(e?.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const isSelf =
    !!selected &&
    selected.username.toLowerCase() ===
      currentUsernameFromStorage().toLowerCase();

  const onAskDelete = async () => {
    if (!selected || isSelf) return;
    setErr(""); setSuccess(""); setPreviewingDelete(true);
    try {
      const preview = await brandsApi.previewDeleteUser(selected.id);
      setDeletePreview(preview);
      setDeleteOpen(true);
    } catch (e: any) {
      setErr(e?.message || "Failed to preview deletion");
    } finally {
      setPreviewingDelete(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!selected) return;
    setDeleteOpen(false);
    setErr(""); setSuccess(""); setDeleting(true);
    const label = selected.full_name || selected.username;
    try {
      // authApi.deleteUser hits the canonical admin endpoint, which runs the
      // full cross-table purge AND revokes the user's tokens.
      await authApi.deleteUser(selected.id);
      setMode("list");
      setSelectedId(null);
      await loadUsers();
      setSuccess(`User ${label} and all their data were permanently deleted.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to delete user");
    } finally {
      setDeleting(false);
      setDeletePreview(null);
    }
  };

  const onResetMfa = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Reset MFA enrollment for ${selected.full_name || selected.username}?\n\n` +
      `Their authenticator app and all backup codes will be invalidated. ` +
      `They'll be required to enroll a new authenticator on their next login.`
    )) return;

    setErr(""); setSuccess(""); setResettingMfa(true);
    try {
      await authApi.updateUser(selected.id, { reset_mfa: true });
      setSuccess("MFA enrollment reset. User will set up a new authenticator on next login.");
      await loadUsers();
    } catch (e: any) {
      setErr(e?.message || "Failed to reset MFA");
    } finally {
      setResettingMfa(false);
    }
  };

  return (
    <div className="px-4 md:px-6 py-5 md:py-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h2
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
              fontWeight: 700,
              margin: 0,
              lineHeight: 1.3,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            User Management
          </h2>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: "var(--muted-foreground)",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            Admin-only. Manage users, roles, credentials, and MFA enrollment.
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto flex-shrink-0">
          {mode !== "create" && (
            <Button
              onClick={() => { setMode("create"); setErr(""); setSuccess(""); }}
              className="w-full sm:w-auto"
              style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}
            >
              <UserPlus style={{ width: 16, height: 16 }} />
              Add user
            </Button>
          )}
          <Button
            onClick={() => setAuditOpen(true)}
            variant="secondary"
            className="w-full sm:w-auto"
            style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}
          >
            <History style={{ width: 16, height: 16 }} />
            Login activity
          </Button>
          <Button
            onClick={loadUsers}
            disabled={loading}
            variant="secondary"
            className="w-full sm:w-auto"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RefreshCcw style={{ width: 16, height: 16 }} />
            Refresh
          </Button>
        </div>
      </div>

      <AuditLogDialog open={auditOpen} onOpenChange={setAuditOpen} />

      {err && (
        <Alert
          variant="destructive"
          style={{
            marginTop: 16,
            backgroundColor: "var(--infrared-coral-5)",
            border: "1px solid var(--infrared-coral)",
          }}
        >
          <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
          <AlertDescription style={{ color: "var(--infrared-coral)" }}>{err}</AlertDescription>
        </Alert>
      )}
      {success && (
        <div
          style={{
            marginTop: 16,
            color: "var(--signal-green)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-sm)",
          }}
        >
          {success}
        </div>
      )}

      {/* ── CREATE MODE ───────────────────────────────────────────────── */}
      {mode === "create" && (
        <CreateUserPage
          onCancel={() => setMode("list")}
          onCreated={(user) => {
            // Reload behind the success step so the list is already correct
            // when the admin comes back to it.
            setSuccess(
              `Created ${user.username}. They must set their own password on first sign-in.`,
            );
            setErr("");
            loadUsers();
          }}
        />
      )}

      {/* ── LIST MODE ─────────────────────────────────────────────────── */}
      {mode === "list" && (
        <>
          {/* Search + filters — above the card grid */}
          <div
            className="mt-4 p-3 md:p-4 rounded-xl"
            style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
            }}
          >
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              {/* Search input */}
              <div className="relative flex-1 md:min-w-[240px]">
                <Search
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 15,
                    height: 15,
                    color: "var(--muted-foreground)",
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email…"
                  style={{
                    width: "100%",
                    padding: "10px 12px 10px 36px",
                    borderRadius: 9,
                    border: "1px solid var(--border)",
                    backgroundColor: "var(--input-background)",
                    fontSize: 13,
                    outline: "none",
                    boxSizing: "border-box",
                    color: "var(--foreground)",
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: 4,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      color: "var(--muted-foreground)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <X style={{ width: 14, height: 14 }} />
                  </button>
                )}
              </div>

              {/* Role filter pills — horizontal scroll on overflow */}
              <div
                className="flex items-center gap-1.5 flex-nowrap overflow-x-auto md:flex-wrap"
                style={{
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                }}
              >
                {(["all", "basic", "standard", "agent", "admin"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setRoleFilter(f)}
                    className="whitespace-nowrap flex-shrink-0"
                    style={{
                      padding: "7px 12px",
                      borderRadius: 9,
                      border: `1px solid ${
                        roleFilter === f ? "var(--alpha-teal)" : "var(--border)"
                      }`,
                      backgroundColor:
                        roleFilter === f ? "var(--alpha-teal-10)" : "var(--muted)",
                      color:
                        roleFilter === f ? "var(--alpha-teal)" : "var(--muted-foreground)",
                      fontSize: 12,
                      fontWeight: roleFilter === f ? 600 : 500,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {f === "all" ? "All roles" : f}
                  </button>
                ))}
              </div>

              {/* Admin-only toggle */}
              <label
                className="flex items-center gap-2 cursor-pointer flex-shrink-0"
                style={{
                  padding: "7px 12px",
                  borderRadius: 9,
                  border: `1px solid ${adminOnly ? "var(--alpha-teal)" : "var(--border)"}`,
                  backgroundColor: adminOnly ? "var(--alpha-teal-10)" : "var(--muted)",
                  fontSize: 12,
                  fontWeight: adminOnly ? 600 : 500,
                  color: adminOnly ? "var(--alpha-teal)" : "var(--muted-foreground)",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={adminOnly}
                  onChange={(e) => setAdminOnly(e.target.checked)}
                  style={{
                    accentColor: "var(--alpha-teal)",
                    width: 14,
                    height: 14,
                    cursor: "pointer",
                  }}
                />
                Admin only
              </label>
            </div>

            {/* Result summary + clear */}
            {!loading && users.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 flex-wrap"
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                }}
              >
                <span>
                  Showing <strong style={{ color: "var(--foreground)" }}>{filteredUsers.length}</strong> of {users.length}{" "}
                  {users.length === 1 ? "user" : "users"}
                </span>
                {hasActiveFilter && (
                  <button
                    onClick={clearFilters}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                      backgroundColor: "transparent",
                      color: "var(--muted-foreground)",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <X style={{ width: 11, height: 11 }} />
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Card grid */}
          {loading ? (
            <div
              className="mt-4 p-5 flex items-center justify-center gap-3"
              style={{
                backgroundColor: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 13 }}>Loading users…</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div
              className="mt-4 p-8 text-center"
              style={{
                backgroundColor: "var(--card)",
                border: "1px dashed var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <UserIcon
                style={{
                  width: 36,
                  height: 36,
                  margin: "0 auto 10px",
                  opacity: 0.4,
                }}
              />
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                {hasActiveFilter ? "No users match your filters" : "No users found"}
              </div>
              <div style={{ fontSize: 12, maxWidth: 360, margin: "0 auto" }}>
                {hasActiveFilter
                  ? "Try adjusting your search, role filter, or admin toggle."
                  : "There are no users to display yet."}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 mt-4">
              {filteredUsers.map((u) => (
                <UserCard key={u.id} user={u} onEdit={goEdit} onFeatureAccess={setFeatureAccessUser} />
              ))}
            </div>
          )}
        </>
      )}

      {featureAccessUser && (
        <FeatureAccessPanel
          userId={featureAccessUser.id}
          username={featureAccessUser.full_name || featureAccessUser.username}
          onClose={() => setFeatureAccessUser(null)}
        />
      )}

      {/* ── EDIT MODE ─────────────────────────────────────────────────── */}
      {mode === "edit" && selected && (
        <div
          className="p-4 md:p-5"
          style={{
            marginTop: 16,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
          }}
        >
          <button
            onClick={goList}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--signal-blue)",
              fontWeight: 700,
              padding: 0,
              marginBottom: 12,
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            Back to user list
          </button>

          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 12,
              color: "var(--foreground)",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            Edit User
            {selected.full_name && (
              <span style={{ color: "var(--muted-foreground)", fontWeight: 500 }}>
                {" — "}
                {selected.full_name}
              </span>
            )}
          </div>

          {/* Full name + Email + Password — all stack on mobile */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label style={{ display: "block", marginBottom: 6 }}>Full name</label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6 }}>Email (username)</label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                type="email"
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6 }}>Reset password</label>
              <Input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Leave blank to keep unchanged"
              />
            </div>
          </div>

          {/* User type selector */}
          <div style={{ marginTop: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              User Role
            </label>
            <p
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginBottom: 10,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              Controls which sections of the app this user can access.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {USER_TYPES.map(({ value, label, description }) => {
                const active = userType === value;
                return (
                  <label
                    key={value}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 10,
                      cursor: "pointer",
                      border: `1px solid ${active ? "var(--alpha-teal)" : "var(--border)"}`,
                      backgroundColor: active ? "var(--alpha-teal-10)" : "var(--muted)",
                      transition: "all 0.15s",
                    }}
                  >
                    <input
                      type="radio"
                      name="user_type"
                      value={value}
                      checked={active}
                      onChange={() => setUserType(value)}
                      style={{
                        accentColor: "var(--alpha-teal)",
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        marginTop: 3,
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: active ? "var(--alpha-teal)" : "var(--foreground)",
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--muted-foreground)",
                          marginTop: 2,
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          lineHeight: 1.4,
                        }}
                      >
                        {description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Admin checkbox */}
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Checkbox
              checked={isAdmin}
              onCheckedChange={(v) => setIsAdmin(v as boolean)}
            />
            <span>Admin user</span>
          </div>

          {/* ── MFA section ─────────────────────────────────────────── */}
          <div
            style={{
              marginTop: 18,
              padding: 14,
              borderRadius: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <ShieldCheck style={{ width: 16, height: 16, color: "var(--alpha-teal)" }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--foreground)" }}>
                Two-factor authentication
              </span>
              <MfaBadge user={selected} />
            </div>

            <p
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginTop: 6,
                marginBottom: 12,
                lineHeight: 1.5,
              }}
            >
              When required, the user must set up an authenticator app (Google
              Authenticator, 1Password, Authy, etc.) before they can access the app.
            </p>

            {/* Require MFA toggle */}
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 8,
                cursor: "pointer",
                backgroundColor: "var(--card)",
                border: "1px solid var(--border)",
              }}
            >
              <Checkbox
                checked={mfaRequired}
                onCheckedChange={(v) => setMfaRequired(v as boolean)}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--foreground)" }}>
                  Require two-factor authentication
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--muted-foreground)",
                    marginTop: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Recommended for all users. Disable only for service accounts
                  or break-glass admin accounts.
                </div>
              </div>
            </label>

            {/* Reset MFA button — only shown if user is currently enrolled */}
            {selected.mfa_enabled && (
              <Button
                type="button"
                variant="secondary"
                onClick={onResetMfa}
                disabled={resettingMfa}
                style={{
                  marginTop: 10,
                  width: "100%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  color: "var(--infrared-coral)",
                  borderColor: "var(--infrared-coral)",
                }}
              >
                {resettingMfa
                  ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
                  : <KeyRound style={{ width: 14, height: 14 }} />}
                Reset MFA enrollment
              </Button>
            )}
          </div>

          {/* Save / Cancel */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <Button
              onClick={onSave}
              disabled={saving}
              className="w-full sm:w-auto"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {saving
                ? <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
                : <Save style={{ width: 16, height: 16 }} />}
              Save changes
            </Button>
            <Button
              onClick={goList}
              variant="secondary"
              disabled={saving}
              className="w-full sm:w-auto"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Cancel
            </Button>
          </div>

          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "var(--muted-foreground)",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            Tip: Leave "Reset password" empty to keep the current password.
          </div>

          {/* ── Danger zone: delete user ───────────────────────────── */}
          <div
            style={{
              marginTop: 20,
              padding: 14,
              borderRadius: 10,
              border: "1px solid var(--infrared-coral)",
              backgroundColor: "var(--infrared-coral-5)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <Trash2 style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--infrared-coral)" }}>
                Danger zone
              </span>
            </div>
            <p
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginTop: 6,
                marginBottom: 12,
                lineHeight: 1.5,
              }}
            >
              Permanently delete this user and every row keyed to their account
              across the platform (settings, monitoring runs, fix actions,
              publishing, agents, …). Shared brand data is preserved. This cannot
              be undone.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={onAskDelete}
              disabled={previewingDelete || deleting || isSelf}
              title={isSelf ? "You cannot delete your own account" : "Delete user"}
              style={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                color: "var(--infrared-coral)",
                borderColor: "var(--infrared-coral)",
              }}
            >
              {previewingDelete || deleting ? (
                <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
              ) : (
                <Trash2 style={{ width: 14, height: 14 }} />
              )}
              {isSelf ? "You cannot delete your own account" : "Delete user"}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          setDeleteOpen(o);
          if (!o) setDeletePreview(null);
        }}
        onConfirm={onConfirmDelete}
        destructive
        title={`Delete ${selected?.full_name || selected?.username || "user"}?`}
        confirmLabel="Delete permanently"
        description={<DeletionImpactDescription preview={deletePreview} noun="user" />}
      />
    </div>
  );
}